<template>
  <ontos-form-dialog v-bind="heading" max-width="1100px"
                     :saving="saving" :save-disabled="edit.unchanged"
                     :full-page-route="fullPageRoute" :dirty="edit.dirty"
                     @save="save()" @closed="$emit('closed')">
    <v-form>
      <!-- alias fixo só na edição: ele é a chave que as coleções guardam -->
      <ontos-workflow-input ref="input" :value="draft" @input="draft = $event" :workflows="workspace.workflows"
                            :fixed-alias="!isCreating" :start-with-auto-alias="isCreating"/>
    </v-form>
  </ontos-form-dialog>
</template>
<script lang="ts">
import {Component, Vue, Prop, toNative} from 'vue-facing-decorator'
import WorkflowInput from '@/components/design/workflow/WorkflowInput.vue'
import Workflow, {defaultWorkflow} from '@/types/Workflow'
import {EnhancedWorkpace} from '@/types/Workspace'
import {deepCopy} from '@/utils/copy'
import {DraftState, draftState} from '@/utils/modelUtils'
import {Heading, workflowCreate, workflowDesign} from '@/utils/heading'

/**
 * Edita o fluxo sem sair da coleção. Casca de modal sobre o mesmo WorkflowInput
 * que a página usa — o par página/modal já existente para instância e Kanban.
 *
 * Quem persiste é o host, que é um WorkspaceRouteComponent e tem a atualização
 * reativa do modelo em memória.
 */
@Component({emits: ['closed', 'create', 'save'], 
  name: 'ontos-workflow-dialog',
  components: {'ontos-workflow-input': WorkflowInput}
})
class WorkflowDialog extends Vue {
  /** undefined = criando; objeto = editando aquele fluxo */
  @Prop({default: undefined}) readonly workflow!: Workflow | undefined
  @Prop({required: true}) readonly workspace!: EnhancedWorkpace
  @Prop({type: Boolean, default: false}) readonly saving!: boolean

  draft: Workflow = defaultWorkflow()

  //Ponto de partida do rascunho: ao criar não há original com que comparar, e a
  //semente é o que o usuário encontrou na tela sem ter mexido em nada.
  seed: Workflow = defaultWorkflow()

  /**
   * No created, e não no inicializador do campo: o inicializador roda sobre a instância
   * crua da classe, onde as props ainda não existem — `this.workflow` viria undefined e
   * EDITAR um fluxo começaria da semente de criação.
   *
   * Na edição, deepCopy e não o objeto do workspace: editar direto mostraria o rascunho
   * na grade de estados por trás do modal. Na criação, a MESMA semente da página de
   * criar — ela mora no types/Workflow.
   */
  created() {
    if (this.workflow) this.draft = deepCopy(this.workflow)
    this.seed = deepCopy(this.draft)
  }

  get isCreating(): boolean {
    return !this.workflow
  }

  /** O mesmo cabeçalho da página de criar: a regra mora no utils/heading */
  get heading(): Heading {
    return this.isCreating ? workflowCreate(this.workspace) : workflowDesign(this.workflow!)
  }

  /** O que mudou, se o salvar habilita e se sair pergunta — a regra mora no `draftState` */
  get edit(): DraftState {
    return draftState(this.workflow, this.draft, this.seed)
  }

  /**
   * O "abrir em página inteira" do modal: a ficha do fluxo quando ele já existe, a
   * página de criar quando ainda não — que é o par da rota que o botão do design do
   * ambiente deixou de usar diretamente. O que estiver digitado aqui não vai junto.
   */
  get fullPageRoute() {
    if (this.isCreating) return {name: 'create-workflow', params: {...this.$route.params}}
    return {name: 'view-workflow', params: {...this.$route.params, workflow: this.workflow!.alias}}
  }

  save() {
    const input = this.$refs.input as any
    if (!input || !input.validateModel()) return
    if (this.isCreating) this.$emit('create', this.draft)
    else this.$emit('save', this.edit.changes)
  }
}

export default toNative(WorkflowDialog)
</script>
